import { Text } from '@chakra-ui/react'
import { useFormContext, useWatch, type Control, type FieldValues } from 'react-hook-form'

interface CharCounterBase {
  /** Maximum allowed characters. */
  max: number
  /** Optional alignment. Defaults to right. */
  align?: 'left' | 'right'
  /** Optional top-margin override. */
  mt?: number
}

type CharCounterProps =
  | (CharCounterBase & { name: string; control?: Control<FieldValues>; value?: never })
  | (CharCounterBase & { name?: never; control?: never; value: string | null | undefined })

/**
 * Lightweight character counter for forms that use raw Chakra Input + register()
 * or local state, instead of the TrackedInput/LabeledInput wrappers (which
 * already include a counter when `maxLength` is set).
 *
 * Two modes:
 *   - Form-managed:  <CharCounter name="legalName" max={500} />
 *                    Requires a <FormProvider> ancestor, or pass `control` explicitly.
 *   - Local-state:   <CharCounter value={newTruck.placa} max={10} />
 */
export default function CharCounter(props: CharCounterProps) {
  const { max, align = 'right', mt = 1 } = props
  if (props.name !== undefined) {
    return <FormCounter name={props.name} control={props.control} max={max} align={align} mt={mt} />
  }
  const value = props.value
  const str = typeof value === 'string' ? value : value == null ? '' : String(value)
  return <CounterText text={str} max={max} align={align} mt={mt} />
}

function FormCounter({ name, control, max, align, mt }: { name: string; control?: Control<FieldValues>; max: number; align: 'left' | 'right'; mt: number }) {
  const ctx = useFormContext()
  const effectiveControl = control ?? ctx?.control
  if (!effectiveControl) return null
  return <FormCounterValue control={effectiveControl} name={name} max={max} align={align} mt={mt} />
}

function FormCounterValue({ control, name, max, align, mt }: { control: Control<FieldValues>; name: string; max: number; align: 'left' | 'right'; mt: number }) {
  const value = useWatch({ control, name }) as unknown
  const text = typeof value === 'string' ? value : value == null ? '' : String(value)
  return <CounterText text={text} max={max} align={align} mt={mt} />
}

function CounterText({ text, max, align, mt }: { text: string; max: number; align: 'left' | 'right'; mt: number }) {
  const count = Math.min(text.length, max)
  const atLimit = count >= max
  return (
    <Text
      fontSize="xs"
      color={atLimit ? '#b23a26' : '#857b71'}
      mt={mt}
      textAlign={align}
      fontVariantNumeric="tabular-nums"
    >
      {count}/{max}
    </Text>
  )
}
